import React, {Component, FC, ReactNode} from "react"
import PropTypes from "prop-types"
import MobileDetect from "mobile-detect"
import {withRouter} from "react-router-dom"
import "react-modern-drawer/dist/index.css"

import {connect} from "@irati/store"
import shouldShowOnboarding from "@irati/onboarding/helpers/shouldRedirectToGDPR"
import Onboarding from "@irati-web/onboarding/src/containers/Onboarding"
import Header from "@irati-web/platform/src/components/Header"
import {ReduxState} from "@irati/store/types"
import {ADMIN_HOME} from "@irati-web/admin/src/routes/routeConfig"
import RenewalsReminderBanner from "@irati-web/admin/src/components/RenewalsReminderBanner"
/* eslint-disable no-restricted-imports */
/** @description: Tech debt, this should be moved outside the ui folder */
import authentication from "@irati/auth"
/* eslint-enable no-restricted-imports */

import styled, {css} from "styled-components"
import {HEADER_DESKTOP_HEIGHT, HEADER_MOBILE_HEIGHT} from "@irati-web/platform/src/components/Header/styled"
import AppNavigation from "@irati-web/platform/src/components/AppNavigation"
import usersService from "@irati/identity/services/users"
import OneTimePassword, {VERIFY_PHONE_NO} from "@irati-web/platform/src/containers/OneTimePassword"
import {hangPromise} from "@irati/utils/promise"
import {getAuthURL} from "@irati/sso/services/sso"
import History from "@irati/utils/history"

import devToggles from "@irati/dev-toggles"
import Flex from "../Flex"
import ShareToDrawer from "../ShareToDrawer"
import Loading from "../Loading"
import Footer from "./Footer"
import Oops from "./Oops"
import MobileDisabled from "./MobileDisabled"
import {Main, NAVIGATION_SIDEBAR_WIDTH, COLLAPSED_SIDEBAR_WIDTH, SECONDARY_NAV_COLLAPSED_SIDEBAR_WIDTH} from "./styled"
import shouldSecondNavigationRender from "./shouldSecondNavigationRender"
import SideBarContext from "./Context/SideBarContext"
import ContentCards from "./ContentCards"
import Version from "./Version"
import DrawerContext, {DrawerData} from "./Context/DrawerContext"
import AppStoreBanner from "./StoreBanner"

interface OptionalProps {
  addContentMarginTop?: boolean
  disableMobile?: boolean
  hasFooter?: boolean
  isFlush?: boolean
  mainBackground?: string
}

interface CollapsedNavs {
  firstNav: boolean
  secondNav: boolean
}

interface Props extends OptionalProps, ReduxState {
  children: ReactNode
}

interface State {
  caught: any
  collapsedNavs: CollapsedNavs
  load: Promise<any> | null
  showDrawer: boolean
  drawerData: DrawerData
  showAppBanner: boolean
  phoneNumber?: string
  ssoUrl?: string
}

interface StyledContentWrapperProps {
  shouldDisplayNavigation?: boolean
  isFullWidth?: boolean
  isAdminSection?: boolean
  addMarginToHeader?: boolean
  width?: number
}

const StyledContentWrapper = styled(Flex)<StyledContentWrapperProps>`
  width: 100%;
  ${({theme, shouldDisplayNavigation, isFullWidth, addMarginToHeader, width}) => css`
    ${addMarginToHeader && `margin-top: ${HEADER_MOBILE_HEIGHT};`}
    ${theme.mediaQueries.minWidthMedia.tablet`
    ${addMarginToHeader && `margin-top: ${HEADER_DESKTOP_HEIGHT};`}
      margin-left: ${shouldDisplayNavigation ? `${width}px` : 0};
      width: ${isFullWidth ? "100%" : `calc(100% - ${width}px)`};
    `}
  `}
`

class AppContainer extends Component<Props, State> {
  static defaultProps: OptionalProps = {
    disableMobile: false,
    hasFooter: true,
    isFlush: false
  }

  static contextTypes = {
    appPath: PropTypes.string.isRequired,
    apps: PropTypes.object.isRequired
  }

  state: State = {
    caught: null,
    width: NAVIGATION_SIDEBAR_WIDTH,
    showDrawer: false,
    drawerData: {
      url: "url",
      title: "title",
      alertText: "alertText",
      source: "source",
      type: "type",
      entity_id: undefined
    },
    collapsedNavs: {
      firstNav: false,
      secondNav: false
    },
    showAppBanner: false,
    load: hangPromise()
  }

  componentDidMount() {
    if (devToggles.isSSOfromVivup()) {
      History.push("/discover", this.context.apps.PERKS)
    }

    const detect = new MobileDetect(window.navigator.userAgent)
    if (detect && !!detect.mobile() && detect.os() === "AndroidOS") {
      this.setState({showAppBanner: true})
    }
    this.setState({
      width: NAVIGATION_SIDEBAR_WIDTH
    })
    if (authentication.isLoggedIn()) {
      getAuthURL(authentication.unAuthedBusiness().uuid, undefined, {}).then(({url}) => {
        this.setState({ssoUrl: url!})
      })
      usersService
        .getUserVerifiedPhoneNo(authentication.user().uuid!)
        .then((res) => {
          this.setState({phoneNumber: res && res.e164PhoneNo ? res.e164PhoneNo : undefined, load: null})
        })
        .catch(() => {
          this.setState({load: null})
        })
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      if (shouldSecondNavigationRender()) {
        return this.handleUpdateNavigationStates({
          firstNav: true,
          secondNav: false
        })
      }
      const {appPath} = this.context
      const pathname = window.location.pathname
      const isHome = pathname === `${appPath}${ADMIN_HOME}`

      this.handleUpdateNavigationStates({
        firstNav: !isHome,
        secondNav: false
      })
    }
  }

  componentDidCatch = (caught: State["caught"]) => {
    this.setState({caught})
  }

  getWidth = () => {
    const {collapsedNavs} = this.state

    if (shouldSecondNavigationRender()) {
      const navOneWidth = collapsedNavs.firstNav ? COLLAPSED_SIDEBAR_WIDTH : NAVIGATION_SIDEBAR_WIDTH
      const navTwoWidth = collapsedNavs.secondNav ? SECONDARY_NAV_COLLAPSED_SIDEBAR_WIDTH : NAVIGATION_SIDEBAR_WIDTH
      return navOneWidth + navTwoWidth
    }
    return collapsedNavs.firstNav ? COLLAPSED_SIDEBAR_WIDTH : NAVIGATION_SIDEBAR_WIDTH
  }

  shouldShowMobileDisabled = () => {
    const {disableMobile, mq} = this.props
    if (!disableMobile) {
      return false
    }

    const detect = new MobileDetect(window.navigator.userAgent)
    return disableMobile && (mq.lessThan.medium || detect.mobile())
  }

  handleUpdateNavigationStates = (collapsedNavs: CollapsedNavs) => {
    this.setState({collapsedNavs})
  }

  render() {
    const {children, isFlush, hasFooter, addContentMarginTop, mainBackground, mq} = this.props
    const {apps, appPath} = this.context
    const {caught, collapsedNavs, showDrawer, drawerData, phoneNumber, load, ssoUrl, showAppBanner} = this.state
    const onboarding = authentication.userOnboarding()
    const isLoggedIn = authentication.isLoggedIn()
    const {ADMIN} = apps
    const isAdminSection = appPath === ADMIN
    const ssoOnly = ssoUrl && !authentication.unAuthedBusiness().showPasswordLogin
    const showOnboarding = onboarding && shouldShowOnboarding(onboarding) && isLoggedIn
    const showCapturePhone =
      isLoggedIn && !authentication.user().isGlobalAdmin && !phoneNumber && !localStorage.getItem(VERIFY_PHONE_NO) && !ssoOnly

    if (showOnboarding) {
      return (
        <>
          {showAppBanner && <AppStoreBanner />}
          <Onboarding />
        </>
      )
    }
    if (showCapturePhone) {
      return (
        <Loading load={load}>
          {showAppBanner && <AppStoreBanner />}
          <OneTimePassword confirmPhone />
        </Loading>
      )
    }

    if (isLoggedIn && !showOnboarding && !showCapturePhone) {
      localStorage.removeItem("login_type")
    }

    const isMobile = mq.lessThan.medium
    const shouldDisplayNavigation = isLoggedIn && !isMobile
    const width = devToggles.isSSOfromVivup() ? 0 : appPath === ADMIN ? this.getWidth() : NAVIGATION_SIDEBAR_WIDTH

    return (
      <SideBarContext.Provider
        value={{
          navWidth: width
        }}>
        <DrawerContext.Provider
          value={{
            setDrawerOpen: (updatedDrawerData: DrawerData) => this.setState({showDrawer: true, drawerData: updatedDrawerData})
          }}>
          {!isLoggedIn && showAppBanner && <AppStoreBanner />}
          <Flex direction="row">
            {!devToggles.isSSOfromVivup() && (
              <AppNavigation
                shouldDisplay={shouldDisplayNavigation}
                onUpdateNavigationStates={this.handleUpdateNavigationStates}
                collapsedNavs={collapsedNavs}
              />
            )}
            <StyledContentWrapper
              direction="column"
              shouldDisplayNavigation={shouldDisplayNavigation}
              addMarginToHeader={!isAdminSection && isLoggedIn}
              isFullWidth={!isLoggedIn}
              width={width}>
              <Header showAppBanner={showAppBanner} />
              <RenewalsReminderBanner />
              <ContentCards />
              <Main
                flush={isFlush || !isLoggedIn}
                marginTop={addContentMarginTop}
                mainBackground={mainBackground}
                className="main"
                showAppBanner={isLoggedIn && showAppBanner}
                width={width}
                isAdminSection={!!isAdminSection}
                shouldDisplayNavigation={shouldDisplayNavigation}>
                {!caught && (this.shouldShowMobileDisabled() ? <MobileDisabled /> : children)}
                {caught && <Oops caught={caught} />}
              </Main>
              {hasFooter && <Footer />}
              <Version />
            </StyledContentWrapper>
          </Flex>
          <ShareToDrawer show={showDrawer} data={drawerData} onClose={() => this.setState({showDrawer: false})} />
        </DrawerContext.Provider>
      </SideBarContext.Provider>
    )
  }
}

const mapStateToProps = (state: ReduxState) => {
  return {
    mq: state.mq
  }
}

const WrappedContainer: FC<OptionalProps> = connect(mapStateToProps)(withRouter(AppContainer)) as any
export default WrappedContainer
